import { MonitorStatus } from '@fe/core';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme
} from '@mui/material';
import { Grid, GridColumn, GridCustomRowProps, GridDataStateChangeEvent, GridNoRecords } from '@progress/kendo-react-grid';
import { useEffect, useMemo, useState } from 'react';
import { FlightStatus } from '../../../common/enums';
import { FleetMonitorFlightDto } from '../../../flights/interop/flightDto';
import { MessageType } from '../../../services/actor-messages';
import { FlightDetailsThresholdList } from '../../components/monitor-status/FlightDetailsThresholdList';
import { EventBus } from '../../contexts/event-bus';
import { useAppSelector } from '../../hooks/store-hooks';
import { useFlightWorker } from '../../hooks/use-flight-worker';
import '../home/panels/fleet/FleetMonitor.css';
import { FleetMonitorItem } from '../home/panels/fleet/FleetMonitorItem';

const PAGE_SIZE = 25;

type Props = {
    monitorStatus: MonitorStatus;
    monitorStatusLabel: string;
    flightStatus: FlightStatus;
    flightStatusLabel: string;
    onClose: () => void;
};

export function FlightStatsDetailsDialog({ monitorStatus, monitorStatusLabel, flightStatus, flightStatusLabel, onClose }: Props) {
    const theme = useTheme();
    const worker = useFlightWorker();
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const lastUpdate = useAppSelector((state) => state.global.flightWorker.lastUpdate?.time);
    const showFlightBgColor = useAppSelector((state) => state.settings.fleetMonitorFlightStatusColorBackground);
    const [skip, setSkip] = useState(0);
    const [result, setResult] = useState<{ data: FleetMonitorFlightDto[]; total: number }>({ data: [], total: 0 });
    const [selectedFlightId, setSelectedFlightId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        worker.actor
            .sendAsync({
                type: MessageType.queryFleetMonitor,
                data: {
                    filter: {
                        logic: 'and',
                        filters: [
                            { field: 'status', operator: 'eq', value: flightStatus },
                            { field: 'monitorStatus', operator: 'eq', value: monitorStatus }
                        ]
                    },
                    sort: [
                        { field: 'normalizedAcid', dir: 'asc' },
                        { field: 'id', dir: 'asc' }
                    ],
                    skip,
                    take: PAGE_SIZE
                }
            })
            .then((response) => {
                if (cancelled) return;
                if (skip > 0 && skip >= response.total) {
                    setSkip(Math.max(0, Math.ceil(response.total / PAGE_SIZE) - 1) * PAGE_SIZE);
                    return;
                }
                setResult({ data: response.data as FleetMonitorFlightDto[], total: response.total });
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setResult({ data: [], total: 0 });
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [worker, monitorStatus, flightStatus, focusedWorkspaceId, lastUpdate, skip, retry]);

    const selectedFlight = result.data.find((flight) => flight.id === selectedFlightId) ?? result.data[0];
    const RowComponent = useMemo(() => {
        const FleetRow = FleetMonitorItem({
            show5thRow: true,
            showFlightBgColor,
            collapsedView: false,
            onClick: setSelectedFlightId,
            onContextMenu: (event, id) => EventBus.fireContextMenu([{ kind: 'flight', flightIds: [id] }], event)
        });
        return function FlightRow(props: GridCustomRowProps) {
            const flight = props.dataItem as FleetMonitorFlightDto;
            const selected = flight.id === selectedFlight?.id;
            return (
                <FleetRow
                    {...props}
                    trProps={{
                        ...props.trProps,
                        tabIndex: 0,
                        'aria-label': `${flight.acid || flight.id}, ${flight.origin || 'N/A'} to ${flight.destination || 'N/A'}`,
                        'aria-selected': selected,
                        className: [props.trProps?.className, selected ? 'selected-light-bg' : ''].filter(Boolean).join(' '),
                        onKeyDown: (event) => {
                            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                event.preventDefault();
                                setSelectedFlightId(flight.id);
                            }
                        }
                    }}
                />
            );
        };
    }, [showFlightBgColor, selectedFlight?.id]);

    const changePage = (event: GridDataStateChangeEvent) => {
        setSelectedFlightId(null);
        setResult({ data: [], total: result.total });
        setSkip(event.dataState.skip ?? 0);
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="flight-stats-details-title">
            <DialogTitle id="flight-stats-details-title">
                {monitorStatusLabel} Monitor Status - {flightStatusLabel} Flights
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {error ? (
                    <Alert severity="error" action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
                        Unable to load flights.
                    </Alert>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' } }}>
                        <Box
                            sx={{
                                minWidth: 0,
                                p: 2,
                                '& .k-grid tr[aria-selected="true"] > td': {
                                    backgroundColor: 'light-dark(var(--color-neutral-700), var(--color-neutral-300))'
                                }
                            }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Flights ({result.total})
                            </Typography>
                            <Grid
                                className="k-grid-no-header k-grid-no-borders"
                                style={{ height: 390 }}
                                data={result.data}
                                dataItemKey="id"
                                total={result.total}
                                skip={skip}
                                take={PAGE_SIZE}
                                pageable={{ buttonCount: 3, pageSizes: false }}
                                onDataStateChange={changePage}
                                rows={{ data: RowComponent }}>
                                <GridNoRecords>{loading ? <CircularProgress size={20} /> : 'No matching flights.'}</GridNoRecords>
                                <GridColumn id="main" field="acid" />
                            </Grid>
                        </Box>
                        <Box
                            sx={{
                                minWidth: 0,
                                p: 2,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: 'divider',
                                overflowX: 'auto'
                            }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Monitor Status - breached thresholds{' '}
                                {selectedFlight ? ` - ${selectedFlight.acid || selectedFlight.id}` : ''}
                            </Typography>
                            {selectedFlight ? (
                                <FlightDetailsThresholdList
                                    key={selectedFlight.id}
                                    flightId={selectedFlight.id}
                                    popoverZIndex={theme.zIndex.modal + 1}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {loading ? <CircularProgress size={20} /> : 'No matching flights.'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
